import React from 'react';
import * as Icons from '@lifeomic/chromicons-native';
import * as CustomIcons from '../CustomIcons';
import { Color, colors } from '../../../utils/theme';
import { View } from 'react-native';

export type IconName = keyof typeof Icons | keyof typeof CustomIcons;

export type IconSize =
    | 'button-icon'
    | 'checkbox-icon'
    | 'tab-icon'
    | 'drawer-icon'
    | 'minimal-button-icon'
    | 'alert-icon'
    | 'large-record-button'
    | 'drawer-button-icon'
    | 'file-picker-icon'
    | 'chat-button-icon'
    | 'avatar-icon'
    | 'card-list-icon'
    | 'folder-picker-icon'
    | 'homepage-icon'
    | 'large-alert-paper-icon'
    | 'card-button-icon'
    | 'header-back-button'
    | 'sort-toggle-not-selected'
    | 'sort-toggle-selected'
    | 'table-action-icon'
    | 'selected-file-icon'
    | 'side-tab-icon'
    | 'ai-search-icon'
    | 'profile-search-result-icon'
    | 'profile-search-prompt-icon'
    | 'dashboard-list-icon'
    | 'ghost-button-icon';
export type IconColorVariant = 'Dark-Text' | 'Gray-Text' | 'Light-Text' | 'Primary' | 'White' | 'Success' | 'Alert';

export type IconProps = {
    name: IconName;
    size: IconSize;
    strokeWidth?: number;
    testID?: string;
} & (
    | {
          colorVariant?: IconColorVariant;
      }
    | { color: Color }
);

type SizeMapItem = {
    size: number;
    strokeWidth?: number;
};

const sizeMap: Record<IconSize, SizeMapItem> = {
    'button-icon': { size: 24 },
    'ghost-button-icon': { size: 18 },
    'checkbox-icon': { size: 12, strokeWidth: 2 },
    'sort-toggle-not-selected': { size: 14 },
    'sort-toggle-selected': { size: 14, strokeWidth: 3 },
    'table-action-icon': { size: 14, strokeWidth: 2 },
    'tab-icon': { size: 30, strokeWidth: 1 },
    'drawer-icon': { size: 24 },
    'minimal-button-icon': { size: 15, strokeWidth: 2 },
    'large-record-button': { size: 40 },
    'alert-icon': { size: 60 },
    'avatar-icon': { size: 30 },
    'drawer-button-icon': { size: 18, strokeWidth: 2 },
    'card-list-icon': { size: 18 },
    'file-picker-icon': { size: 26 },
    'chat-button-icon': { size: 20 },
    'folder-picker-icon': { size: 36 },
    'homepage-icon': { size: 24, strokeWidth: 2 },
    'large-alert-paper-icon': { size: 28 },
    'card-button-icon': { size: 48 },
    'header-back-button': { size: 40, strokeWidth: 1.3 },
    'selected-file-icon': { size: 32 },
    'side-tab-icon': { size: 16, strokeWidth: 2 },
    'ai-search-icon': { size: 22, strokeWidth: 2 },
    'profile-search-result-icon': { size: 28, strokeWidth: 2 },
    'profile-search-prompt-icon': { size: 24, strokeWidth: 2 },
    'dashboard-list-icon': { size: 18, strokeWidth: 2 },
};

const Icon: React.FC<IconProps> = (props) => {
    const IconComponent = props.name in Icons ? (Icons as any)[props.name] : (CustomIcons as any)[props.name];
    const size = sizeMap[props.size];
    const color = 'color' in props ? colors[props.color] : colors[props.colorVariant || 'Dark-Text'];
    return (
        <View testID={props.testID}>
            <IconComponent
                width={size.size}
                height={size.size}
                color={color}
                strokeWidth={props.strokeWidth || size.strokeWidth}
            />
        </View>
    );
};

export default React.memo(Icon);

export type AlertIconVariant = 'success' | 'error' | 'warning' | 'info';

export type AlertIconProps = {
    variant: AlertIconVariant;
    name: IconName;
    size?: AlertIconSize;
};

export type AlertIconSize = 'xs' | 'lg';

type AlertIconVariantColors = {
    background: Color;
    icon: Color;
};

const alertIconColors: Record<AlertIconVariant, AlertIconVariantColors> = {
    success: {
        background: 'Success',
        icon: 'White',
    },
    error: {
        background: 'Alert',
        icon: 'White',
    },
    warning: {
        background: 'Primary-Reg',
        icon: 'Dark-Text',
    },
    info: {
        background: 'White',
        icon: 'Primary',
    },
};

type SizeMapEntry = {
    width: number;
    height: number;
    borderRadius: number;
    iconSize: IconSize;
};

const alertIconSizeMap: Record<AlertIconSize, SizeMapEntry> = {
    xs: {
        width: 18,
        height: 18,
        borderRadius: 9,
        iconSize: 'checkbox-icon',
    },
    lg: {
        width: 70,
        height: 70,
        borderRadius: 35,
        iconSize: 'alert-icon',
    },
};

export const AlertIcon: React.FC<AlertIconProps> = (props) => {
    const variantColors = alertIconColors[props.variant];
    const sizes = alertIconSizeMap[props.size || 'lg'];

    return (
        <View
            style={{
                width: sizes.width,
                height: sizes.height,
                borderRadius: sizes.borderRadius,
                backgroundColor: colors[variantColors.background],
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <Icon name={props.name} size={sizes.iconSize} color={variantColors.icon} />
        </View>
    );
};
